// Services.js - Improved with better Learn More buttons and modal
import { useEffect, useRef, useState } from 'react';
import { Building2, Home, Warehouse, Store, DollarSign, FileCheck, ChevronDown, CheckCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Services() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeService, setActiveService] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: Building2,
      title: 'Owner-Occupied Commercial Real Estate',
      description: 'Purchase or refinance the property where your business operates.',
      color: 'from-blue-500 to-blue-600',
      details: {
        overview: 'Stop paying rent and build equity in your own commercial space. We help business owners secure financing for office buildings, retail spaces, industrial properties, and more.',
        highlights: [
          'Financing for purchase or refinance',
          'Up to 100% loan-to-value (LTV)',
          'Terms up to 25 years',
          'Support for incorporated professionals and SMEs'
        ],
        benefits: [
          'Build business equity instead of paying rent',
          'Stable occupancy costs',
          'Potential tax advantages',
          'Asset appreciation benefits'
        ],
        process: 'We assess your business financials, property value, and cash flow to structure the optimal financing solution.',
        eligibility: 'Established businesses with 2+ years of operation, good credit history, and stable revenue.'
      }
    },
    {
      icon: Home,
      title: 'Multi-Unit Residential (5+ Units)',
      description: 'Financing for apartment buildings and residential rental properties.',
      color: 'from-emerald-500 to-emerald-600',
      details: {
        overview: 'Specialized financing solutions for multi-family residential properties with 5 or more units, including CMHC-insured and conventional options.',
        highlights: [
          'CMHC and conventional financing options',
          'Financing for 5 to 100+ unit properties',
          'Acquisition, refinance, and construction loans',
          'Bridge financing available'
        ],
        benefits: [
          'Diversified rental income streams',
          'Economies of scale in management',
          'Strong historical appreciation',
          'Multiple revenue units per property'
        ],
        process: 'Property valuation, rental income analysis, lender matching, and streamlined application process.',
        eligibility: 'Experienced investors or new entrants with strong financial backing.'
      }
    },
    {
      icon: Warehouse,
      title: 'Income Producing Commercial Properties',
      description: 'Financing for properties generating rental income from commercial tenants.',
      color: 'from-teal-500 to-teal-600',
      details: {
        overview: 'Strategic financing for commercial properties that generate stable rental income from business tenants.',
        highlights: [
          'Based on property income and valuation',
          'Debt service coverage ratio (DSCR) focused',
          'Multiple tenant diversification benefits',
          'Long-term lease consideration'
        ],
        benefits: [
          'Passive income generation',
          'Professional tenant relationships',
          'Triple net lease opportunities',
          'Inflation-protected revenue'
        ],
        process: 'Cash flow analysis, tenant credit evaluation, property condition assessment, and lender negotiation.',
        eligibility: 'Properties with stable occupancy history and qualified commercial tenants.'
      }
    },
    {
      icon: Store,
      title: 'Retail & Hospitality Financing',
      description: 'Specialized solutions for retail centers, restaurants, and hotels.',
      color: 'from-cyan-500 to-cyan-600',
      details: {
        overview: 'Tailored financing for consumer-facing businesses including shopping centers, standalone retail, restaurants, and accommodation properties.',
        highlights: [
          'Business real property financing',
          'Equipment and leasehold improvements',
          'Seasonal cash flow accommodation',
          'Brand franchise financing'
        ],
        benefits: [
          'Prime location acquisition support',
          'Brand value recognition',
          'Tourism and traffic flow analysis',
          'Seasonal business understanding'
        ],
        process: 'Business performance review, market analysis, brand evaluation, and specialized lender matching.',
        eligibility: 'Established retail/hospitality businesses with proven operating history.'
      }
    },
    {
      icon: DollarSign,
      title: 'Canada Small Business Financing Program loans',
      description: 'makes it easier for small businesses to get loans from financial institutions',
      color: 'from-indigo-500 to-indigo-600',
      details: {
        overview: 'Government-backed loan program helping small businesses access affordable financing for real estate, equipment, and leasehold improvements.',
        highlights: [
          'Up to $1,000,000 for real estate',
          'Up to $350,000 for equipment',
          'Government registration fee: 2%',
          'Lender interest rate maximum: Prime + 3%'
        ],
        benefits: [
          'Government risk sharing with lenders',
          'Easier qualification criteria',
          'Fixed and floating rate options',
          'No personal net worth requirements'
        ],
        process: 'Application preparation, government registration, lender submission, and approval coordination.',
        eligibility: 'Small businesses with gross annual revenues under $10 million.'
      }
    },
    {
      icon: FileCheck,
      title: 'Commercial Refinancing',
      description: 'Optimize your existing commercial mortgage terms.',
      color: 'from-violet-500 to-violet-600',
      details: {
        overview: 'Strategic refinancing solutions to lower payments, access equity, extend terms, or consolidate business debt.',
        highlights: [
          'Rate reduction opportunities',
          'Equity extraction for business growth',
          'Term extension options',
          'Debt consolidation capabilities'
        ],
        benefits: [
          'Immediate cash flow improvement',
          'Access to property appreciation',
          'Debt structure optimization',
          'Interest cost reduction'
        ],
        process: 'Current loan assessment, market rate analysis, lender competition, and optimal structure determination.',
        eligibility: 'Properties with equity and owners with good payment history.'
      }
    }
  ];

  const toggleServiceDetails = (index) => {
    setActiveService(activeService === index ? null : index);
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 }
  };

  return (
    <section id="services" ref={sectionRef} className="py-20 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Service Detail Modal */}
      <AnimatePresence>
        {activeService !== null && (
          <motion.div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-y-auto w-full"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex justify-between items-center shadow-sm">
                <h3 className="text-2xl font-bold text-slate-900">{services[activeService].title}</h3>
                <button 
                  onClick={() => setActiveService(null)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors duration-200"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-lg font-semibold mb-4 text-slate-900 border-b pb-2">Overview</h4>
                    <p className="text-slate-600 mb-6 leading-relaxed">{services[activeService].details.overview}</p>
                    
                    <h4 className="text-lg font-semibold mb-4 text-slate-900 border-b pb-2">Highlights</h4>
                    <ul className="space-y-3 mb-6">
                      {services[activeService].details.highlights.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold mb-4 text-slate-900 border-b pb-2">Benefits</h4>
                    <ul className="space-y-3 mb-6">
                      {services[activeService].details.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <h4 className="text-lg font-semibold mb-4 text-slate-900 border-b pb-2">Process</h4>
                    <p className="text-slate-600 mb-4 leading-relaxed">{services[activeService].details.process}</p>
                    
                    <h4 className="text-lg font-semibold mb-4 text-slate-900 border-b pb-2">Eligibility</h4>
                    <p className="text-slate-600 leading-relaxed">{services[activeService].details.eligibility}</p>
                  </div>
                </div>
                
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <button 
                    onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4 rounded-lg font-semibold hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Get Started with {services[activeService].title}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Our <span className="text-emerald-500">Services</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            From owner-operated properties to income-producing investments, our solutions cover every commercial financing need
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-200 flex flex-col h-full"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>

                <div className="relative p-6 flex-1 flex flex-col">
                  <div className={`w-14 h-14 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200 shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>

                  <h3 className="text-xl font-semibold text-slate-900 mb-3 leading-tight">
                    {service.title}
                  </h3>

                  <p className="text-slate-600 leading-relaxed mb-4 flex-1">
                    {service.description}
                  </p>

                  {/* Improved Learn More Button - Always at bottom */}
                  <div className="mt-auto pt-4 border-t border-slate-100">
                    <button 
                      onClick={() => toggleServiceDetails(index)}
                      className="w-full text-emerald-600 hover:text-emerald-700 font-semibold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center group border border-emerald-200 hover:border-emerald-300 bg-emerald-50 hover:bg-emerald-100"
                    >
                      Learn More
                      <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-300 ${activeService === index ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Stats */}
        <motion.div 
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <div className="text-2xl md:text-3xl font-bold text-emerald-600 mb-2">$500M+</div>
            <div className="text-slate-600 font-medium">Commercial Loans Funded</div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <div className="text-2xl md:text-3xl font-bold text-emerald-600 mb-2">1000+</div>
            <div className="text-slate-600 font-medium">Businesses Served</div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <div className="text-2xl md:text-3xl font-bold text-emerald-600 mb-2">50+</div>
            <div className="text-slate-600 font-medium">Lender Partners</div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <div className="text-2xl md:text-3xl font-bold text-emerald-600 mb-2">98%</div>
            <div className="text-slate-600 font-medium">Approval Rate</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}